import { c as _c } from "react/compiler-runtime";
import { useState, type ReactNode, type MouseEvent, type ElementType } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTheme, styled } from '@classify/react-core-components/styles';
import Callout from '@classify/react-core-components/Callout';
import LinkIcon from '@mui/icons-material/Link';
import Dialog from '@classify/react-core-components/Dialog';
import Typography from '@classify/react-core-components/Typography';
import Stack from '@classify/react-core-components/Stack';
import Switch from '@classify/react-core-components/Switch';
const defaultTranslations = {
  title: 'Leaving OpenClassrooms',
  content: {
    intro: `You are now leaving our platform to visit:`,
    warning: `For your safety, please be cautious about sharing personal information on external sites. We cannot verify the security or content of third-party websites.
Are you sure you want to proceed?`
  },
  actions: {
    cancel: 'Cancel',
    confirm: 'Proceed',
    remember: "Don't warn me again for this site"
  }
};
const ImportantURLPartToCheck = styled('span')(({
  theme
}) => ({
  flexShrink: 0,
  fontWeight: theme.typography.fontWeightBold
}));
const ProtocolSeparator = styled('span')({
  flexShrink: 0
});
const PathName = styled('span')(({
  theme
}) => ({
  color: theme.palette.text.secondary,
  fontSize: theme.typography.caption.fontSize,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0
}));
export interface LinkGuardProps {
  children: ReactNode;
  component?: ElementType;
  translations?: typeof defaultTranslations;
  onHostTrustChange?: (host: string, isTrusted: boolean) => void;
  trustedHosts?: string[];
  onBlock?: (url: URL) => void;
  onCancel?: (url: URL) => void;
  onProceed?: (url: URL) => void;
}
const LinkGuard = t0 => {
  const $ = _c(50);
  const {
    children,
    component: t1,
    translations: t2,
    onHostTrustChange,
    trustedHosts: t3,
    onBlock,
    onCancel,
    onProceed
  } = t0;
  const Component = t1 === undefined ? "div" : t1;
  const translations = t2 === undefined ? defaultTranslations : t2;
  let t4;
  if ($[0] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const trustedHosts = t4;
  const [blockedURL, setBlockedURL] = useState(null);
  const [shouldOpenInNewTab, setShouldOpenInNewTab] = useState(false);
  const [isWarningDisplayed, setIsWarningDisplayed] = useState(false);
  const [shouldTrustCurrentBlockedHost, setShouldTrustCurrentBlockedHost] = useState(false);
  const {
    palette
  } = useTheme();
  let t5;
  if ($[2] !== onBlock || $[3] !== trustedHosts) {
    t5 = e => {
      const {
        target
      } = e;
      const closestAnchor = (target as HTMLElement).closest("a");
      setShouldTrustCurrentBlockedHost(false);
      if (!closestAnchor) {
        return;
      }
      const url = new URL(closestAnchor.href);
      const targetHost = url.host;
      if (trustedHosts.includes(targetHost)) {
        return;
      }
      const currentHost = window.location.host;
      const shouldWarn = targetHost !== currentHost;
      if (shouldWarn) {
        setBlockedURL(url);
        setIsWarningDisplayed(true);
        setShouldOpenInNewTab(closestAnchor.target === "_blank");
        e.preventDefault();
        onBlock?.(url);
      }
    };
    $[2] = onBlock;
    $[3] = trustedHosts;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleClickDelegation = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsWarningDisplayed(false);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const closeWarning = t6;
  let t7;
  if ($[6] !== blockedURL || $[7] !== onHostTrustChange || $[8] !== onProceed || $[9] !== shouldOpenInNewTab || $[10] !== shouldTrustCurrentBlockedHost) {
    t7 = () => {
      if (blockedURL) {
        if (shouldOpenInNewTab) {
          window.open(blockedURL.href, "_blank", "noopener,noreferrer");
          closeWarning();
        } else {
          window.location.href = blockedURL.href;
        }
        onHostTrustChange?.(blockedURL.host, shouldTrustCurrentBlockedHost);
        onProceed?.(blockedURL);
      }
    };
    $[6] = blockedURL;
    $[7] = onHostTrustChange;
    $[8] = onProceed;
    $[9] = shouldOpenInNewTab;
    $[10] = shouldTrustCurrentBlockedHost;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7 || $[13] !== translations.actions.confirm) {
    t8 = {
      children: translations.actions.confirm,
      variant: "primary",
      onClick: t7
    };
    $[12] = t7;
    $[13] = translations.actions.confirm;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== blockedURL || $[16] !== onCancel) {
    t9 = () => {
      onCancel?.(blockedURL);
      closeWarning();
    };
    $[15] = blockedURL;
    $[16] = onCancel;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9 || $[19] !== translations.actions.cancel) {
    t10 = {
      children: translations.actions.cancel,
      onClick: t9
    };
    $[18] = t9;
    $[19] = translations.actions.cancel;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== translations.content.intro) {
    t11 = <Typography variant="body1">{translations.content.intro}</Typography>;
    $[21] = translations.content.intro;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== blockedURL || $[24] !== palette) {
    t12 = blockedURL && <Callout icon={LinkIcon} type="info"><Stack style={{
        fontFamily: "monospace",
        overflow: "hidden",
        width: "100%"
      }} direction="row" gap={0.5}><ImportantURLPartToCheck style={{
          color: blockedURL.protocol === "https:" ? "inherit" : palette.notification.danger.main
        }}>{blockedURL.protocol}</ImportantURLPartToCheck><ProtocolSeparator>{"//"}</ProtocolSeparator><ImportantURLPartToCheck>{blockedURL.hostname}</ImportantURLPartToCheck><PathName>{blockedURL.pathname}</PathName></Stack></Callout>;
    $[23] = blockedURL;
    $[24] = palette;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== translations.content.warning) {
    t13 = <Typography variant="body1">{translations.content.warning}</Typography>;
    $[26] = translations.content.warning;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== blockedURL || $[29] !== onHostTrustChange || $[30] !== translations.actions.remember) {
    t14 = onHostTrustChange && blockedURL && <FormControlLabel control={<Switch defaultChecked={false} onChange={e_0 => setShouldTrustCurrentBlockedHost(e_0.target.checked)} />} label={translations.actions.remember} />;
    $[28] = blockedURL;
    $[29] = onHostTrustChange;
    $[30] = translations.actions.remember;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t14) {
    t15 = <Stack gap={1} direction="column">{t11}{t12}{t13}{t14}</Stack>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== isWarningDisplayed || $[38] !== t10 || $[39] !== t15 || $[40] !== t8 || $[41] !== translations) {
    t16 = <Dialog isOpen={isWarningDisplayed} onClose={closeWarning} translations={translations} confirmButtonProps={t8} dismissButtonProps={t10}>{t15}</Dialog>;
    $[37] = isWarningDisplayed;
    $[38] = t10;
    $[39] = t15;
    $[40] = t8;
    $[41] = translations;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== Component || $[44] !== children || $[45] !== handleClickDelegation) {
    t17 = <Component onClick={handleClickDelegation}>{children}</Component>;
    $[43] = Component;
    $[44] = children;
    $[45] = handleClickDelegation;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t16 || $[48] !== t17) {
    t18 = <>{t16}{t17}</>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  return t18;
};
export default LinkGuard;